import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, FileType2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const clean = (s) => String(s ?? "").replace(/\s+/g, " ").trim();
const visible = (el) => !!(el.offsetParent || el.getClientRects().length);

// Reads every visible <table> in the container as { headers, rows }.
const readTables = (root) => [...root.querySelectorAll("table")].filter(visible).map((t) => {
  const head = t.querySelector("thead tr");
  const headers = head ? [...head.children].map((c) => clean(c.innerText)) : [];
  const rows = [...t.querySelectorAll("tbody tr, tr")].filter((r) => r.parentElement.tagName !== "THEAD" && visible(r))
    .map((r) => [...r.children].map((c) => clean(c.innerText))).filter((r) => r.some(Boolean));
  return { headers, rows };
}).filter((t) => t.rows.length);

const save = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000); };
const num = (v) => (/^-?[\d,]+(\.\d+)?$/.test(v) ? Number(v.replace(/,/g, "")) : v);

const toExcel = async (tables, title, file) => {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  tables.forEach((t, i) => {
    const ws = wb.addWorksheet(tables.length > 1 ? `جدول ${i + 1}` : "التقرير", { views: [{ rightToLeft: true }] });
    ws.addRow([title]).font = { bold: true, size: 14 };
    ws.addRow([`تاريخ التصدير: ${new Date().toLocaleString("ar-SA-u-nu-latn")}`]);
    ws.addRow([]);
    if (t.headers.length) { const h = ws.addRow(t.headers); h.font = { bold: true, color: { argb: "FFFFFFFF" } }; h.eachCell((c) => { c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F766E" } }; }); }
    t.rows.forEach((r) => ws.addRow(r.map(num)));
    const n = Math.max(t.headers.length, ...t.rows.map((r) => r.length));
    for (let c = 1; c <= n; c++) ws.getColumn(c).width = Math.min(40, Math.max(10, ...[t.headers[c - 1] || "", ...t.rows.map((r) => r[c - 1] || "")].map((v) => String(v).length + 2)));
  });
  save(new Blob([await wb.xlsx.writeBuffer()], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${file}.xlsx`);
};

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;");
const toWord = (tables, title, file) => {
  const body = tables.map((t) => `<table>${t.headers.length ? `<tr>${t.headers.map((h) => `<th>${esc(h)}</th>`).join("")}</tr>` : ""}${t.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</table><br/>`).join("");
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>@page{size:A4 landscape;margin:1.5cm}body{direction:rtl;font-family:Tahoma,Arial;font-size:11pt}table{border-collapse:collapse;width:100%}th{background:#0F766E;color:#fff}th,td{border:1px solid #999;padding:4px 6px;text-align:right}</style></head>
<body dir="rtl"><h2>${esc(title)}</h2><p>تاريخ التصدير: ${new Date().toLocaleString("ar-SA-u-nu-latn")}</p>${body}</body></html>`;
  save(new Blob(["\ufeff", html], { type: "application/msword" }), `${file}.doc`);
};

const toPDF = async (root, file) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const canvas = await html2canvas(root, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff", windowWidth: Math.max(root.scrollWidth, 1200) });
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = 297 - 16, H = 210 - 16, pxPerMm = canvas.width / W, pageH = Math.floor(H * pxPerMm);
  for (let y = 0, i = 0; y < canvas.height; y += pageH, i++) {
    const c = document.createElement("canvas"); c.width = canvas.width; c.height = Math.min(pageH, canvas.height - y);
    c.getContext("2d").drawImage(canvas, 0, y, canvas.width, c.height, 0, 0, canvas.width, c.height);
    if (i) pdf.addPage();
    pdf.addImage(c.toDataURL("image/jpeg", 0.85), "JPEG", 8, 8, W, c.height / pxPerMm);
  }
  pdf.save(`${file}.pdf`);
};

export const ReportExport = ({ getRoot, title }) => {
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    const root = getRoot();
    if (!root) return;
    const file = `${title}-${new Date().toISOString().slice(0, 10)}`.replace(/[\\/:*?"<>|]/g, "-");
    setBusy(kind);
    try {
      if (kind === "pdf") await toPDF(root, file);
      else {
        const tables = readTables(root);
        if (!tables.length) { toast.error("لا يوجد جدول في هذه الصفحة — جرّب التصدير PDF"); return; }
        if (kind === "xlsx") await toExcel(tables, title, file); else toWord(tables, title, file);
      }
      toast.success("تم التصدير");
    } catch { toast.error("تعذّر التصدير"); } finally { setBusy(""); }
  };
  return (
    <DropdownMenu dir="rtl">
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" data-testid="report-export-btn" disabled={!!busy} className="h-9 shrink-0 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تصدير</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="report-export-xlsx" onClick={() => run("xlsx")} className="gap-2"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel</DropdownMenuItem>
        <DropdownMenuItem data-testid="report-export-doc" onClick={() => run("doc")} className="gap-2"><FileText className="h-4 w-4 text-blue-600" /> Word</DropdownMenuItem>
        <DropdownMenuItem data-testid="report-export-pdf" onClick={() => run("pdf")} className="gap-2"><FileType2 className="h-4 w-4 text-red-600" /> PDF</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
